import { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import pushNotificationService from '../services/pushNotificationService';

export const usePushNotifications = () => {
  const token = useSelector(state => state.user.token);
  const [subscription, setSubscription] = useState(null);
  const [permission, setPermission] = useState(
    typeof Notification !== 'undefined' ? Notification.permission : 'default'
  );
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const initPushNotifications = async () => {
      const initialized = await pushNotificationService.init();
      if (initialized) {
        const sub = await pushNotificationService.getSubscription();
        setSubscription(sub);
      }
    };

    initPushNotifications();
  }, []);

  const subscribe = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Request permission if needed
      if (permission !== 'granted') {
        const newPermission = await pushNotificationService.requestPermission();
        setPermission(newPermission);
        
        if (newPermission !== 'granted') {
          throw new Error('Notification permission denied');
        }
      }

      // Subscribe to push notifications
      const sub = await pushNotificationService.subscribe(token);
      setSubscription(sub);
      
      return true;
    } catch (err) {
      setError(err.message);
      console.error('Push notification subscription failed:', err);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [permission, token]);

  const unsubscribe = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      await pushNotificationService.unsubscribe(token);
      setSubscription(null);
      return true;
    } catch (err) {
      setError(err.message);
      console.error('Push notification unsubscription failed:', err);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  return {
    subscription,
    permission,
    isLoading,
    error,
    subscribe,
    unsubscribe,
    isSupported: pushNotificationService.isSupported
  };
};